/**
 * OpenRouter model catalog — one shared copy for the whole app.
 *
 * The catalog is served by ``/api/settings/openrouter-models``, itself backed
 * by a 24h server-side cache of OpenRouter's public ``/api/v1/models``. It was
 * previously fetched into ``ModelSelector``'s local component state, which
 * meant nothing outside that dropdown could see a model's real context window
 * — so ``getContextLimit`` had no entry for any ``openrouter/*`` id and fell
 * through to the 200K default. A 1M-context model like Kimi K3 rendered as
 * "… / 200K" in the Context tab.
 *
 * Hoisting it here gives ``modelLimits`` a synchronous read
 * (``useModelCatalogStore.getState()``) and lets components subscribe so they
 * re-render when the catalog lands after first paint.
 *
 * Deliberately *not* part of ``chatStore``: this is provider metadata that
 * outlives any one conversation, and keeping it separate avoids an import
 * cycle (``chatStore`` → ``modelLimits`` → here).
 */

import { create } from "zustand";

import { apiFetch } from "@/api/client";

/**
 * A catalog row as returned by ``/settings/openrouter-models``. A subset of
 * the normalised fields in ``cowork/ui/openrouter_catalog.py::_normalize`` —
 * enough to render, rank, and size a model.
 */
export interface OpenRouterModel {
  id: string;            // "openrouter/anthropic/claude-opus-4.8"
  label: string;         // "Anthropic: Claude Opus 4.8"
  tier: string;
  cost_class: number;
  supports_reasoning: boolean;
  /** Tokens. ``null`` when OpenRouter publishes no window for the model. */
  context_window: number | null;
  best_for: string;
  /** OpenRouter's per-token price strings (USD/token). */
  pricing?: { prompt?: string; completion?: string };
}

interface ModelCatalogState {
  models: OpenRouterModel[];
  /** ``id`` → context window, for the synchronous ``getContextLimit`` read. */
  contextWindows: Record<string, number>;
  isLoading: boolean;
  /** True once a fetch has settled (either way), so ``load`` is idempotent. */
  loaded: boolean;

  /**
   * Fetch the catalog once per page load. Repeat calls are no-ops unless
   * ``force`` is set, so every mounting consumer can call it unconditionally.
   * Never throws — a failure leaves the catalog empty and the callers degrade
   * (picker shows a hint, limits fall back to the static table).
   */
  load: (force?: boolean) => Promise<void>;
}

export const useModelCatalogStore = create<ModelCatalogState>((set, get) => ({
  models: [],
  contextWindows: {},
  isLoading: false,
  loaded: false,

  load: async (force = false) => {
    const { loaded, isLoading } = get();
    if (isLoading || (loaded && !force)) return;
    set({ isLoading: true });
    try {
      const res = await apiFetch<{ models: OpenRouterModel[] }>(
        "/settings/openrouter-models",
      );
      const models = res.models ?? [];
      const contextWindows: Record<string, number> = {};
      for (const m of models) {
        if (typeof m.context_window === "number" && m.context_window > 0) {
          contextWindows[m.id] = m.context_window;
        }
      }
      set({ models, contextWindows, loaded: true, isLoading: false });
    } catch {
      // Non-fatal: Claude still works and the OpenRouter group shows a hint.
      set({ loaded: true, isLoading: false });
    }
  },
}));

/**
 * Context window for an OpenRouter id, or ``null`` if the catalog hasn't
 * loaded or doesn't publish one. Synchronous by design — ``getContextLimit``
 * is a pure function called from render paths and store reducers.
 */
export function openRouterContextWindow(modelId: string): number | null {
  return useModelCatalogStore.getState().contextWindows[modelId] ?? null;
}
